import { useId, useLayoutEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';

import {
  type CanvasLayoutHeaderContentSlot,
  useCanvasLayoutContext,
} from './canvasLayoutContext';

interface CanvasLayoutHeaderContentProps {
  children: ReactNode;
  slot?: CanvasLayoutHeaderContentSlot;
  minVisibleWidth?: number;
  onVisibilityChange?: (isVisible: boolean) => void;
}

export default function CanvasLayoutHeaderContent({
  children,
  slot = 'end',
  minVisibleWidth,
  onVisibilityChange,
}: CanvasLayoutHeaderContentProps) {
  const id = useId();
  const [availableWidth, setAvailableWidth] = useState(0);
  const {
    startHeaderContentContainer,
    centerHeaderContentContainer,
    endHeaderContentContainer,
    registerHeaderContent,
    clearHeaderContent,
  } = useCanvasLayoutContext();
  const headerContentContainer = {
    start: startHeaderContentContainer,
    center: centerHeaderContentContainer,
    end: endHeaderContentContainer,
  }[slot];
  const hasChildren = children != null;
  const isVisible =
    hasChildren &&
    headerContentContainer !== null &&
    (minVisibleWidth == null ||
      availableWidth === 0 ||
      availableWidth >= minVisibleWidth);

  useLayoutEffect(() => {
    if (!hasChildren) {
      return;
    }

    registerHeaderContent(id, slot);

    return () => {
      clearHeaderContent(id, slot);
    };
  }, [clearHeaderContent, hasChildren, id, registerHeaderContent, slot]);

  useLayoutEffect(() => {
    if (headerContentContainer === null) {
      setAvailableWidth(0);
      return;
    }

    const measure = () => {
      setAvailableWidth(headerContentContainer.getBoundingClientRect().width);
    };

    measure();

    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    const observer = new ResizeObserver(() => {
      measure();
    });
    observer.observe(headerContentContainer);

    return () => {
      observer.disconnect();
    };
  }, [headerContentContainer]);

  useLayoutEffect(() => {
    onVisibilityChange?.(isVisible);
  }, [isVisible, onVisibilityChange]);

  if (!isVisible) {
    return null;
  }

  return createPortal(children, headerContentContainer);
}
